import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from '../icons/icons.js';
import styles from './fields.module.css';

/**
 * TESSMA ONE D-8 form controls — STATUS: APPROVED AND FROZEN (v1.1).
 *
 * Accessibility is a property of the primitive, not of the screen that uses it:
 * every field wires label, helper and error together with `aria-describedby`,
 * sets `aria-invalid`, and marks the error region `role="alert"` so a validation
 * failure is announced rather than merely coloured.
 */

export type FieldState = 'default' | 'warning' | 'error';

export interface FieldShellProps {
  readonly label: string;
  readonly htmlFor: string;
  readonly helper?: ReactNode;
  readonly message?: ReactNode;
  readonly state?: FieldState;
  readonly required?: boolean;
  readonly optional?: boolean;
  readonly helperId: string;
  readonly messageId: string;
  readonly children: ReactNode;
}

/** Shared label / helper / message frame. Every control uses it, so the
 *  vertical rhythm and the assistive wiring can never drift between fields. */
export function FieldShell({
  label,
  htmlFor,
  helper,
  message,
  state = 'default',
  required = false,
  optional = false,
  helperId,
  messageId,
  children,
}: FieldShellProps) {
  return (
    <div className={styles.field}>
      <label id={`${htmlFor}-label`} className={styles.label} htmlFor={htmlFor}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        )}
        {optional && <span className={styles.optional}>(optional)</span>}
      </label>

      {children}

      {helper !== undefined && message === undefined && (
        <span className={styles.helper} id={helperId}>
          {helper}
        </span>
      )}

      {message !== undefined && (
        <span
          className={[
            styles.message,
            state === 'error' ? styles.messageError : '',
            state === 'warning' ? styles.messageWarning : '',
          ]
            .filter(Boolean)
            .join(' ')}
          id={messageId}
          role={state === 'error' ? 'alert' : undefined}
        >
          <span className={styles.messageIcon} aria-hidden="true">
            {state === 'error' ? (
              <Icon name="warning-circle" size={13} />
            ) : state === 'warning' ? (
              <Icon name="warning" size={13} />
            ) : null}
          </span>
          {message}
        </span>
      )}
    </div>
  );
}

function controlClasses(
  state: FieldState,
  extra: ReadonlyArray<string | false | undefined>,
): string {
  return [
    styles.control,
    state === 'error' ? styles.invalid : '',
    state === 'warning' ? styles.warned : '',
    ...extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/** Shared props for every labelled control. */
interface CommonFieldProps {
  readonly label: string;
  readonly helper?: ReactNode;
  readonly message?: ReactNode;
  readonly state?: FieldState;
  readonly optional?: boolean;
}

/* ------------------------------------------------------------------ */
/* Text, textarea                                                      */
/* ------------------------------------------------------------------ */

export interface TextFieldProps
  extends CommonFieldProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {}

export function TextField({
  label,
  helper,
  message,
  state = 'default',
  optional,
  id,
  ...rest
}: TextFieldProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const helperId = `${fieldId}-helper`;
  const messageId = `${fieldId}-message`;

  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      helper={helper}
      message={message}
      state={state}
      required={rest.required === true}
      optional={optional === true}
      helperId={helperId}
      messageId={messageId}
    >
      <input
        id={fieldId}
        className={controlClasses(state, [])}
        aria-invalid={state === 'error' ? true : undefined}
        aria-describedby={
          message !== undefined ? messageId : helper !== undefined ? helperId : undefined
        }
        {...rest}
      />
    </FieldShell>
  );
}

export interface TextAreaProps
  extends CommonFieldProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {}

export function TextArea({
  label,
  helper,
  message,
  state = 'default',
  optional,
  id,
  ...rest
}: TextAreaProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const helperId = `${fieldId}-helper`;
  const messageId = `${fieldId}-message`;

  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      helper={helper}
      message={message}
      state={state}
      required={rest.required === true}
      optional={optional === true}
      helperId={helperId}
      messageId={messageId}
    >
      <textarea
        id={fieldId}
        className={controlClasses(state, [styles.textarea])}
        aria-invalid={state === 'error' ? true : undefined}
        aria-describedby={
          message !== undefined ? messageId : helper !== undefined ? helperId : undefined
        }
        {...rest}
      />
    </FieldShell>
  );
}

/* ------------------------------------------------------------------ */
/* Numeric family: number, money, percentage                           */
/* ------------------------------------------------------------------ */

export interface NumericFieldProps extends TextFieldProps {
  /** Rendered inside the control, e.g. a currency symbol or a percent sign. */
  readonly affix?: string;
  readonly affixPosition?: 'leading' | 'trailing';
}

function NumericField({
  label,
  helper,
  message,
  state = 'default',
  optional,
  affix,
  affixPosition = 'leading',
  id,
  ...rest
}: NumericFieldProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const helperId = `${fieldId}-helper`;
  const messageId = `${fieldId}-message`;
  const leading = affixPosition === 'leading';

  const input = (
    <input
      id={fieldId}
      inputMode="decimal"
      className={controlClasses(state, [
        styles.numeric,
        affix !== undefined && leading ? styles.hasLeadingAffix : '',
        affix !== undefined && !leading ? styles.hasTrailingAffix : '',
      ])}
      aria-invalid={state === 'error' ? true : undefined}
      aria-describedby={
        message !== undefined ? messageId : helper !== undefined ? helperId : undefined
      }
      {...rest}
    />
  );

  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      helper={helper}
      message={message}
      state={state}
      required={rest.required === true}
      optional={optional === true}
      helperId={helperId}
      messageId={messageId}
    >
      {affix === undefined ? (
        input
      ) : (
        <span className={styles.affixed}>
          {input}
          <span
            className={[styles.affix, leading ? styles.affixLeading : styles.affixTrailing].join(
              ' ',
            )}
            aria-hidden="true"
          >
            {affix}
          </span>
        </span>
      )}
    </FieldShell>
  );
}

export function NumberField(props: Omit<NumericFieldProps, 'affix' | 'affixPosition'>) {
  return <NumericField {...props} />;
}

/**
 * Monetary input. The currency symbol is presentation only — the value is a
 * decimal string that the platform converts to integer minor units before it
 * reaches the domain, because money is never a float (ADR-0020).
 *
 * `currency` is additive and opt-in (owner decision S3-5): given an ISO-4217
 * code, the symbol is derived through `Intl` for the supplied locale, so a
 * screen does not have to carry a symbol table. Omitted, the control renders
 * exactly the frozen `€` default it did at v1.1 and no baseline moves.
 * `currencySymbol` still wins if both are given, because an explicit symbol is
 * a deliberate override.
 */
export function MoneyField({
  currencySymbol,
  currency,
  locale = 'en-GB',
  ...props
}: NumericFieldProps & {
  readonly currencySymbol?: string;
  /** ISO-4217 code; the symbol is derived from it. */
  readonly currency?: string;
  readonly locale?: string;
}) {
  const affix =
    currencySymbol ?? (currency === undefined ? '€' : currencySymbolFor(locale, currency));
  return <NumericField affix={affix} affixPosition="leading" {...props} />;
}

/**
 * The symbol CLDR uses for a currency in a locale — `£` for GBP in en-GB, `US$`
 * for USD in en-GB but `$` in en-US. Derived rather than tabulated, because a
 * table in this repository would be one more thing to keep current.
 */
function currencySymbolFor(locale: string, currency: string): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? currency;
  } catch {
    // An unknown code renders as the code itself rather than throwing inside a
    // render: a wrong-looking label is a defect report, a blank screen is not.
    return currency;
  }
}

export function PercentField(props: Omit<NumericFieldProps, 'affix' | 'affixPosition'>) {
  return <NumericField affix="%" affixPosition="trailing" {...props} />;
}

/* ------------------------------------------------------------------ */
/* Read-only presentation for controls with no native read-only mode   */
/* ------------------------------------------------------------------ */

/**
 * Owner decision (review rounds 1 and 2): read-only is not disabled. Select,
 * combobox, date and date range have no native `readonly`, so they share this
 * one presentation: the field frame at `--color-workspace`, full-contrast ink,
 * the frozen focus ring kept, and `aria-readonly` on an exposed textbox role so
 * assistive technology announces "read only" rather than "dimmed" or "disabled".
 *
 * It is focusable on purpose. A value a user cannot reach with the keyboard is
 * a value they cannot copy.
 *
 * The name comes from `aria-labelledby`, not from the field's `<label for>`:
 * `for` only names a labelable element, and a div is not one. FieldShell gives
 * every label the id `${htmlFor}-label`, which is what this points at.
 */
export function ReadOnlyValue({
  id,
  value,
  placeholder = '—',
  icon,
  wrap = false,
  describedBy,
}: {
  readonly id: string;
  /** Rendered content. A string is the common case; chips are the other. */
  readonly value: ReactNode;
  readonly placeholder?: string;
  readonly icon?: IconName;
  readonly wrap?: boolean;
  readonly describedBy?: string;
}) {
  const empty = value === undefined || value === null || value === '';
  return (
    <div
      id={id}
      className={[styles.readOnly, wrap ? styles.readOnlyWrap : ''].filter(Boolean).join(' ')}
      tabIndex={0}
      role="textbox"
      aria-readonly="true"
      aria-labelledby={`${id}-label`}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
    >
      {icon !== undefined && (
        <span className={styles.readOnlyIcon}>
          <Icon name={icon} size={15} />
        </span>
      )}
      {empty ? <span className={styles.readOnlyEmpty}>{placeholder}</span> : value}
    </div>
  );
}

/** The drawn mark of a read-only choice control — information, not a control. */
function ChoiceMark({ checked, radio }: { readonly checked: boolean; readonly radio: boolean }) {
  return (
    <span
      className={[
        styles.readOnlyMark,
        radio ? styles.readOnlyMarkRadio : '',
        checked ? styles.readOnlyMarkOn : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden="true"
    />
  );
}

export interface ReadOnlyChoiceItem {
  readonly label: ReactNode;
  readonly checked: boolean;
  readonly description?: ReactNode;
}

/**
 * Read-only display of a checkbox set or a radio group.
 *
 * `aria-readonly` is placed exactly where ARIA 1.2 supports it and nowhere
 * else: on `radiogroup` for a radio set, and on each `checkbox` for a checkbox
 * set. `role="group"` and `role="radio"` do not support it, and asserting it
 * there is an error an audit will — correctly — flag.
 */
export function ReadOnlyChoices({
  id,
  label,
  items,
  radio = false,
  describedBy,
}: {
  readonly id: string;
  /** Names the group. A set of values with no name is a list of orphans. */
  readonly label?: string;
  readonly items: ReadonlyArray<ReadOnlyChoiceItem>;
  readonly radio?: boolean;
  readonly describedBy?: string;
}) {
  return (
    <div
      id={id}
      className={styles.readOnlyChoices}
      tabIndex={0}
      role={radio ? 'radiogroup' : 'group'}
      {...(radio ? { 'aria-readonly': true } : {})}
      {...(label === undefined ? {} : { 'aria-label': label })}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
    >
      {items.map((item, index) => (
        <span
          key={index}
          className={styles.readOnlyChoice}
          role={radio ? 'radio' : 'checkbox'}
          aria-checked={item.checked}
          {...(radio ? {} : { 'aria-readonly': true })}
        >
          <ChoiceMark checked={item.checked} radio={radio} />
          <span>
            {item.label}
            {item.description !== undefined && (
              <span className={styles.choiceDescription}>{item.description}</span>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Select                                                              */
/* ------------------------------------------------------------------ */

export interface SelectFieldProps
  extends CommonFieldProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly placeholder?: string;
  /**
   * HTML gives <select> no readonly attribute, so it is declared here and
   * handled by rendering the shared read-only presentation instead. Disabling
   * the control is explicitly not the fallback — see ReadOnlyValue.
   */
  readonly readOnly?: boolean;
}

/** A native select: fully accessible, keyboard-complete, and it uses the
 *  platform picker on touch devices where that is what users expect. */
export function SelectField({
  label,
  helper,
  message,
  state = 'default',
  optional,
  options,
  placeholder,
  id,
  readOnly = false,
  ...rest
}: SelectFieldProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const helperId = `${fieldId}-helper`;
  const messageId = `${fieldId}-message`;
  const selectedValue =
    typeof rest.value === 'string'
      ? rest.value
      : typeof rest.defaultValue === 'string'
        ? rest.defaultValue
        : undefined;

  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      helper={helper}
      message={message}
      state={state}
      required={rest.required === true}
      optional={optional === true}
      helperId={helperId}
      messageId={messageId}
    >
      {readOnly ? (
        /* A native <select> has no readonly. Rather than fall back to disabled —
           which the owner has ruled out — show the chosen label in the shared
           read-only frame and keep the value submittable through a hidden
           input, so the form still posts what the user sees. */
        <>
          <ReadOnlyValue
            id={fieldId}
            value={
              options.find((option) => option.value === selectedValue)?.label ?? placeholder ?? ''
            }
            {...(message !== undefined
              ? { describedBy: messageId }
              : helper !== undefined
                ? { describedBy: helperId }
                : {})}
          />
          {rest.name !== undefined && (
            <input type="hidden" name={rest.name} value={selectedValue ?? ''} />
          )}
        </>
      ) : (
        <span className={styles.selectWrap}>
          <select
            id={fieldId}
            className={controlClasses(state, [styles.select])}
            aria-invalid={state === 'error' ? true : undefined}
            aria-describedby={
              message !== undefined ? messageId : helper !== undefined ? helperId : undefined
            }
            {...rest}
          >
            {placeholder !== undefined && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className={styles.selectCaret} aria-hidden="true">
            <Icon name="caret-down" size={12} />
          </span>
        </span>
      )}
    </FieldShell>
  );
}

/* ------------------------------------------------------------------ */
/* Checkbox, radio, toggle                                             */
/* ------------------------------------------------------------------ */

export interface ChoiceProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type'
> {
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly indeterminate?: boolean;
}

export function Checkbox({ label, description, indeterminate = false, ...rest }: ChoiceProps) {
  return (
    <label className={styles.choice}>
      <input
        type="checkbox"
        className={styles.choiceInput}
        ref={(node) => {
          if (node !== null) node.indeterminate = indeterminate;
        }}
        {...rest}
      />
      <span>
        {label}
        {description !== undefined && (
          <span className={styles.choiceDescription}>{description}</span>
        )}
      </span>
    </label>
  );
}

export function Radio({ label, description, ...rest }: Omit<ChoiceProps, 'indeterminate'>) {
  return (
    <label className={styles.choice}>
      <input type="radio" className={`${styles.choiceInput} ${styles.choiceRadio}`} {...rest} />
      <span>
        {label}
        {description !== undefined && (
          <span className={styles.choiceDescription}>{description}</span>
        )}
      </span>
    </label>
  );
}

export interface ToggleProps {
  readonly label: ReactNode;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly describedBy?: string;
}

/** A switch, not a checkbox: `role="switch"` so it is announced as on/off. */
export function Toggle({ label, checked, onChange, disabled = false, describedBy }: ToggleProps) {
  const id = useId();
  return (
    <span className={styles.toggle}>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={describedBy}
        disabled={disabled}
        className={styles.toggleTrack}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.toggleThumb} />
      </button>
      <label htmlFor={id} id={`${id}-label`}>
        {label}
      </label>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Grouping                                                            */
/* ------------------------------------------------------------------ */

export interface ChoiceGroupProps {
  readonly legend: string;
  readonly helper?: ReactNode;
  readonly message?: ReactNode;
  readonly state?: FieldState;
  readonly inline?: boolean;
  readonly children: ReactNode;
}

/** A real `fieldset`/`legend`, so the group name is announced with each option. */
export function ChoiceGroup({
  legend,
  helper,
  message,
  state = 'default',
  inline = false,
  children,
}: ChoiceGroupProps) {
  return (
    <fieldset className={styles.group}>
      <legend className={styles.groupLegend}>{legend}</legend>
      <div
        className={[styles.groupOptions, inline ? styles.groupInline : '']
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
      {helper !== undefined && message === undefined && (
        <span className={styles.helper}>{helper}</span>
      )}
      {message !== undefined && (
        <span
          className={[
            styles.message,
            state === 'error' ? styles.messageError : styles.messageWarning,
          ].join(' ')}
          role={state === 'error' ? 'alert' : undefined}
        >
          <span className={styles.messageIcon} aria-hidden="true">
            <Icon name={state === 'error' ? 'warning-circle' : 'warning'} size={13} />
          </span>
          {message}
        </span>
      )}
    </fieldset>
  );
}
